import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Switch } from '@/components/forms/switch'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'

import {
  applicationChannels,
  getAdminAppDetail,
  updateAdminAppChannels,
  type AdminApp,
  type ApplicationChannel,
} from './api'

export function AdminAppChannelsDrawer({
  app,
  onOpenChange,
}: {
  app: AdminApp
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('adminApps')
  const detail = useQuery({
    queryKey: ['admin-app-detail', app.id],
    queryFn: () => getAdminAppDetail(app.id),
    refetchOnWindowFocus: false,
    staleTime: 0,
  })

  if (!detail.isFetching && !detail.isError && Array.isArray(detail.data?.allowed_channels)) {
    return (
      <ChannelsForm
        app={app}
        initialChannels={detail.data.allowed_channels}
        onClose={() => onOpenChange(false)}
      />
    )
  }
  return (
    <Drawer
      onOpenChange={onOpenChange}
      open
      size="sm"
      title={`${t('channels.title')} / ${app.name}`}
    >
      {detail.isFetching ? (
        <p className="text-sm text-[var(--color-text-muted)]" role="status">
          {t('channels.loading')}
        </p>
      ) : (
        <ErrorState onRetry={() => void detail.refetch()} title={t('channels.loadFailed')} />
      )}
    </Drawer>
  )
}

function ChannelsForm({
  app,
  initialChannels,
  onClose,
}: {
  app: AdminApp
  initialChannels: ApplicationChannel[]
  onClose: () => void
}) {
  const { t } = useTranslation('adminApps')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const formId = useId()
  const [selected, setSelected] = useState(initialChannels)
  const mutation = useMutation({
    mutationFn: () => updateAdminAppChannels(app.id, selected),
    onSuccess: () => {
      showFeedback({ message: t('channels.saved'), scope: 'global', tone: 'success' })
      void queryClient.invalidateQueries({
        predicate: (query) =>
          [
            'admin-apps',
            'admin-app-detail',
            'user-apps',
            'user-app-tags',
            'user-business-navigation',
            'chat-runtime-app',
            'chat-view-config',
          ].includes(String(query.queryKey[0])),
      })
      onClose()
    },
  })

  return (
    <Drawer
      footer={
        <>
          <Button disabled={mutation.isPending} onClick={onClose} size="md" variant="secondary">
            {t('channels.cancel')}
          </Button>
          <Button disabled={mutation.isPending} form={formId} size="md" type="submit">
            {mutation.isPending ? t('channels.saving') : t('channels.save')}
          </Button>
        </>
      }
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose()
      }}
      open
      size="sm"
      title={`${t('channels.title')} / ${app.name}`}
    >
      <form
        id={formId}
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          mutation.mutate()
        }}
      >
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('channels.description')}
        </p>
        <div className="divide-y divide-[var(--color-divider)]">
          {applicationChannels.map((channel) => (
            <div className="flex items-center justify-between gap-5 py-4" key={channel}>
              <div>
                <p className="text-sm font-medium">{t(`channels.options.${channel}.label`)}</p>
                <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
                  {t(`channels.options.${channel}.description`)}
                </p>
              </div>
              <Switch
                aria-label={t(`channels.options.${channel}.label`)}
                checked={selected.includes(channel)}
                disabled={mutation.isPending}
                onCheckedChange={(checked) => {
                  setSelected((current) =>
                    applicationChannels.filter((value) =>
                      value === channel ? checked : current.includes(value),
                    ),
                  )
                }}
              />
            </div>
          ))}
        </div>
        {selected.length === 0 ? (
          <p className="rounded-lg bg-[var(--color-surface-muted)] p-3 text-sm" role="status">
            {t('channels.allClosed')}
          </p>
        ) : null}
        {mutation.isError ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('channels.saveFailed')}
          </p>
        ) : null}
      </form>
    </Drawer>
  )
}
